import {
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { createEditEnvValidationSchema } from '../../../../@xmcloud/yup-validations';
import {
	FormikErrors,
	FormikHandlers,
	FormikState,
	FormikTouched,
	useFormik,
} from 'formik';
import {
	ESourceControl,
	SourceControlType,
	TenantType,
} from '../../../../@xmcloud/types';
import { EnvVariable } from '../../../features/custom-environment-variables';
import { useValidateSourceControlIntegrationV2 } from '../../../services/repositories';
import { useGetSourceControlAccessToken } from '../../../services/accessToken';
import { IGetEnvironmentResponseV2 } from '../../../models/environmentModel';
import {
	EnvironmentTypesEnum,
	SOURCE_CONTROL_INTEGRATION_ID,
} from '../../create-project/helpers';
import { useGetProjectEnvironmentsList } from '../../../services/projects';
import { useGetAllEnvironmentsLimitation } from '../../../services/environments';
import { useParams } from 'react-router-dom';
import { handleUpdatedValues } from '../helper';
import { useProjectDetails } from '../../../../@xmcloud/hooks';
import {
	getProviderFromRepository,
	isConnectionFromAnotherOrg,
} from '../sourceControlHelpers';

export type FormikValues = {
	provider: SourceControlType;
	sourceControlIntegrationId: string;
	sourceControlIntegrationName: string;
	repository: string;
	repositoryId: string;
	environmentName: string;
	deployOnCommit: boolean;
	tenantType: TenantType;
	repositoryBranch: string;
	adoOrganization: string;
	adoProjectName: string;
	cmEnvironmentId: string;
	availableCmEnvironmentName: string;
	environmentVariables: EnvVariable[];
	repositoryOwner: string;
	environmentType: EnvironmentTypesEnum | '';
};

type CreateEditEnvContextType = {
	errors: FormikErrors<FormikValues>;
	values: FormikValues;
	handleChange: FormikHandlers['handleChange'];
	formikHandleSubmit: FormikHandlers['handleSubmit'];
	validateForm: (
		values?: FormikValues,
	) => Promise<FormikErrors<FormikValues>>;
	setFieldError: (field: string, message: string) => void;
	setFieldValue: (field: string, value: any) => void;
	setFieldTouched: any;
	touched: FormikTouched<FormikValues>;
	resetForm: (s?: Partial<FormikState<FormikValues>> | undefined) => void;
	setValues: (
		values: React.SetStateAction<FormikValues>,
		shouldValidate?: boolean | undefined,
	) => Promise<void> | Promise<FormikErrors<FormikValues>>;

	setTouched: any;
	setErrors: (errors: FormikErrors<FormikValues>) => void;
	isADOProvider: boolean;
	isGithubProvider: boolean;
	isLoading: boolean;
	accessToken: string;
	isIntegrationIdValidationValid: boolean;
	integrationValidationErrorMsg: string;
	isCm: boolean;
	isCreate: boolean;
	initialFormValues: FormikValues;
	onClose: () => void;
	initialLoading: boolean;
	/** So the account dropdown can show the initial connection when it is from another org */
	initialIntegrationId: string;
	initialIntegrationLabel: string;
	/** Provider of the initial connection; only show "initial connection" option when it matches selected provider */
	initialIntegrationProvider: SourceControlType | '';
	/** When true, show connection message as warning (no red border) and allow repo/branch fallbacks */
	isConnectionFromAnotherOrg: boolean;
	/** Env repo details; when user selects back the "another org" connection, set these so form matches initial state */
	initialRepository: string;
	initialRepositoryId: string;
	initialRepositoryBranch: string;
	initialAdoOrganization: string;
	initialAdoProjectName: string;
};

export const CreateEditEnvContext = createContext<CreateEditEnvContextType>(
	{} as CreateEditEnvContextType,
);

const { ADO, GitHub } = ESourceControl;

const providerList = {
	1: GitHub,
	2: ADO,
	'': '',
};

export const initialValues: FormikValues = {
	provider: GitHub,
	sourceControlIntegrationName: '',
	sourceControlIntegrationId: '',
	repository: '',
	repositoryId: '',
	environmentName: '',
	repositoryBranch: '',
	adoProjectName: '',
	adoOrganization: '',
	deployOnCommit: false,
	environmentVariables: [],
	availableCmEnvironmentName: '',
	tenantType: 0,
	cmEnvironmentId: '',
	repositoryOwner: '',
	environmentType: '',
};

export const CreateEditEnvironmentProvider: React.FC<{
	children: ReactNode;
	isCm: boolean;
	isCreate: boolean;
	environment?: IGetEnvironmentResponseV2;
	onClose: () => void;
}> = ({ children, isCm, isCreate, environment, onClose }) => {
	const { projectId } = useParams<{ projectId: string }>();
	const currentProvider = useRef('');
	const [initialFormValues, setInitialFormValues] = useState(initialValues);

	const { project, isLoading: isProjectDetailsLoading } = useProjectDetails(
		projectId!,
	);

	const { data: environmentsData, isLoading: isEnvironmentsLoading } =
		useGetProjectEnvironmentsList({
			projectId: projectId!,
			//if there is no environment in (eh/cm)current environment creation, we need to fetch all environments
			enabled: !environment,
		});

	const environments = environmentsData?.data || [];
	const backupEnv = environments[0];

	const {
		data: allEnvironmentsLimitation,
		isLoading: isAllEnvironmentLimitationLoading,
	} = useGetAllEnvironmentsLimitation(isCreate);

	const validationSchema = createEditEnvValidationSchema({
		isAdoProvider: currentProvider.current === ADO,
		allEnvironmentsLimitation,
		isCreate,
		isCm,
	});

	const {
		errors,
		values,
		handleChange,
		setFieldError,
		setFieldValue,
		setFieldTouched,
		setValues,
		touched,
		handleSubmit: formikHandleSubmit,
		validateForm,
		resetForm,
		setTouched,
		setErrors,
	} = useFormik({
		initialValues,
		validationSchema,
		onSubmit: (values: FormikValues) => {},
	});
	const sourceControlIntegrationId =
		values.sourceControlIntegrationId ||
		environment?.repositoryDetails?.sourceControlIntegrationId ||
		backupEnv?.repositoryDetails?.sourceControlIntegrationId ||
		project?.sourceControlIntegrationId ||
		'';

	const {
		isFetching: isSourceControlIntegrationFetching,
		isLoading: isSourceControlIntegrationLoading,
		isIntegrationIdValidationValid,
		integrationValidationErrorMsg,
	} = useValidateSourceControlIntegrationV2({
		integrationId: sourceControlIntegrationId,
		enabled: !!sourceControlIntegrationId,
	});

	const shouldCallAccessTokenApi =
		isIntegrationIdValidationValid && !isSourceControlIntegrationFetching;

	const {
		data: accessTokenData,
		isLoading: isAccessTokenLoading,
		isFetching: isAccessTokenFetching,
	} = useGetSourceControlAccessToken(
		sourceControlIntegrationId,
		shouldCallAccessTokenApi,
	);

	const accessToken = accessTokenData?.data.accessToken || '';

	const providerCode = accessTokenData?.data.provider || '';
	const providerName =
		providerList[providerCode as keyof typeof providerList];

	const currentEnv = environment || backupEnv;
	const repositoryForPrefill =
		currentEnv?.repositoryDetails?.repository || project?.repository || '';
	const derivedProvider = getProviderFromRepository(repositoryForPrefill);
	const providerForPrefill = providerName || derivedProvider;

	const initialIntegrationId =
		currentEnv?.repositoryDetails?.sourceControlIntegrationId ||
		project?.sourceControlIntegrationId ||
		'';
	const initialIntegrationLabel =
		currentEnv?.repositoryDetails?.sourceControlIntegrationName ||
		project?.sourceControlIntegrationName ||
		'';
	const initialIntegrationProvider: SourceControlType =
		providerForPrefill === GitHub || providerForPrefill === ADO
			? providerForPrefill
			: '';

	const isAnotherOrgConnection = isConnectionFromAnotherOrg(
		integrationValidationErrorMsg,
	);

	useEffect(() => {
		if (
			!integrationValidationErrorMsg ||
			isConnectionFromAnotherOrg(integrationValidationErrorMsg)
		) {
			setFieldError(SOURCE_CONTROL_INTEGRATION_ID, undefined);
			return;
		}

		setFieldError(
			SOURCE_CONTROL_INTEGRATION_ID,
			integrationValidationErrorMsg,
		);
		setFieldTouched(SOURCE_CONTROL_INTEGRATION_ID, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [integrationValidationErrorMsg]);

	useEffect(() => {
		const currentEnv = environment || backupEnv;
		const isBackupEnv = !!backupEnv && !environment;

		if (providerForPrefill && !initialFormValues.environmentType) {
			const updatedValues = handleUpdatedValues({
				env: currentEnv,
				project,
				providerName: String(providerForPrefill),
				isCreate,
				isCm,
				isBackupEnv,
			});

			setValues((prev: any) => ({
				...prev,
				...updatedValues,
			}));
			setInitialFormValues((prev: any) => ({
				...prev,
				...updatedValues,
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [providerForPrefill]);

	currentProvider.current = values.provider;

	const isLoading =
		isSourceControlIntegrationFetching ||
		isAccessTokenFetching ||
		isAllEnvironmentLimitationLoading ||
		isEnvironmentsLoading ||
		isProjectDetailsLoading;

	const initialLoading =
		(isAllEnvironmentLimitationLoading ||
			isSourceControlIntegrationLoading ||
			isAccessTokenLoading ||
			isEnvironmentsLoading ||
			isProjectDetailsLoading) &&
		!initialFormValues.environmentType;

	return (
		<CreateEditEnvContext.Provider
			value={{
				errors,
				values,
				handleChange,
				formikHandleSubmit,
				validateForm,
				setFieldError,
				setFieldValue,
				setFieldTouched,
				touched,
				resetForm,
				setValues,
				setTouched,
				setErrors,
				isADOProvider: values.provider === ADO,
				isGithubProvider: values.provider === GitHub,
				isLoading,
				accessToken,
				isIntegrationIdValidationValid,
				integrationValidationErrorMsg,
				isCm,
				isCreate,
				initialFormValues,
				onClose,
				initialLoading,
				initialIntegrationId,
				initialIntegrationLabel,
				initialIntegrationProvider,
				isConnectionFromAnotherOrg: isAnotherOrgConnection,
				initialRepository: initialFormValues.repository,
				initialRepositoryId: initialFormValues.repositoryId,
				initialRepositoryBranch: initialFormValues.repositoryBranch,
				initialAdoOrganization: initialFormValues.adoOrganization,
				initialAdoProjectName: initialFormValues.adoProjectName,
			}}
		>
			{children}
		</CreateEditEnvContext.Provider>
	);
};

export function useCreateEditEnvContext(): CreateEditEnvContextType {
	const context = useContext(CreateEditEnvContext);

	if (context === undefined) {
		throw new Error(
			'useCreateEditEnv must be used within a CreateEditEnvironmentProvider',
		);
	}

	return context;
}
